const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'div',
  'em',
  'figcaption',
  'figure',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'section',
  'span',
  'strong',
  'ul',
]);

const GLOBAL_ATTRS = new Set(['class', 'title']);
const TAG_ATTRS = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height', 'loading']),
};
const BLOCK_TAGS = new Set(['blockquote', 'div', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'li', 'ol', 'p', 'section', 'ul']);
const EDITOR_NOISE_CLASSES = new Set(['editor-paragraph']);

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isSafeUrl(value, allowImageData = false) {
  const url = String(value || '').trim().replace(/[\u0000-\u001F\u007F\s]+/g, '');
  if (!url) return false;
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../') || url.startsWith('#')) return true;

  try {
    const parsed = new URL(url, window.location.origin);
    const protocol = parsed.protocol.toLowerCase();
    if (['http:', 'https:', 'mailto:', 'tel:'].includes(protocol)) return true;
    if (allowImageData && protocol === 'data:') {
      return /^data:image\/(?:png|jpe?g|gif|webp|avif);base64,/i.test(url);
    }
    return false;
  } catch {
    return false;
  }
}

function sanitizeElement(element) {
  const tagName = element.tagName.toLowerCase();

  if (['script', 'style', 'template'].includes(tagName)) {
    element.remove();
    return;
  }

  if (!ALLOWED_TAGS.has(tagName)) {
    element.replaceWith(...Array.from(element.childNodes));
    return;
  }

  Array.from(element.attributes).forEach(attr => {
    const name = attr.name.toLowerCase();
    const value = attr.value;
    const allowedAttrs = TAG_ATTRS[tagName] || new Set();

    if (name.startsWith('on') || (!GLOBAL_ATTRS.has(name) && !allowedAttrs.has(name))) {
      element.removeAttribute(attr.name);
      return;
    }

    if ((tagName === 'a' && name === 'href') && !isSafeUrl(value)) {
      element.removeAttribute(attr.name);
      return;
    }

    if ((tagName === 'img' && name === 'src') && !isSafeUrl(value, true)) {
      element.removeAttribute(attr.name);
      return;
    }

    if (tagName === 'a' && name === 'target' && value === '_blank') {
      element.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

function cleanClassAttribute(element) {
  const className = element.getAttribute('class');
  if (className === null) return;

  const classes = className
    .split(/\s+/)
    .map(cls => cls.trim())
    .filter(cls => cls && !EDITOR_NOISE_CLASSES.has(cls));

  if (classes.length) {
    element.setAttribute('class', classes.join(' '));
  } else {
    element.removeAttribute('class');
  }
}

function hasBlockChild(element) {
  return Array.from(element.children).some(child => BLOCK_TAGS.has(child.tagName.toLowerCase()));
}

function hasRenderableContent(element) {
  if (element.querySelector('img, br')) return true;
  return String(element.textContent || '').replace(/\u00a0/g, ' ').trim().length > 0;
}

function unwrapElement(element) {
  element.replaceWith(...Array.from(element.childNodes));
}

function removeEmptyNoiseElements(root) {
  Array.from(root.querySelectorAll('span, p, div'))
    .reverse()
    .forEach(element => {
      if (element.attributes.length === 0 && !hasRenderableContent(element)) {
        element.remove();
      }
    });
}

export function normalizeRichHtml(value) {
  const html = String(value || '');
  if (!html) return '';

  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return html;
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  Array.from(root.querySelectorAll('*')).forEach(element => {
    element.removeAttribute('style');
    cleanClassAttribute(element);

    const tagName = element.tagName.toLowerCase();
    if (tagName === 'span' && (element.attributes.length === 0 || hasBlockChild(element))) {
      unwrapElement(element);
    }
  });

  removeEmptyNoiseElements(root);
  return root.innerHTML;
}

export function sanitizeHtml(value) {
  const html = normalizeRichHtml(value);
  if (!html) return '';

  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return escapeHtml(html);
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  Array.from(root.querySelectorAll('*')).forEach(sanitizeElement);

  // Ensure all images have explicit dimensions to prevent CLS
  Array.from(root.querySelectorAll('img:not([width]):not([height])')).forEach(img => {
    img.setAttribute('width', '384');
    img.setAttribute('height', '384');
  });

  return root.innerHTML;
}

